import { Resident } from "../../../API";
import { StripedDataGrid } from "../../grids/StripedDataGrid";
import React, { useMemo, useState } from "react";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";

export interface PageInfo {
  totalRowCount: number | undefined;
}
interface ResidentDataGridProps {
  residents: Array<Resident | null>;
  pageSize: number;
  onPaginationModelChange: (model: GridPaginationModel) => Promise<PageInfo>;
  isLoading: boolean;
}

const ResidentDataGrid: React.FC<ResidentDataGridProps> = ({
  residents,
  pageSize,
  onPaginationModelChange,
  isLoading,
}) => {
  const [totalRowCount, setTotalRowCount] = React.useState<
    number | undefined
  >();

  const handlePaginationChange = async (model: GridPaginationModel) => {
    // resonsible for handling changes in pagination model
    const trc = (await onPaginationModelChange(model)).totalRowCount;
    if (!totalRowCount) {
      setTotalRowCount(trc);
    }
  };

  const filteredResidents = residents.filter(
    (resident): resident is Resident => resident !== null
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "community",
        width: 125,
        headerName: "Community",
        headerClassName: "app-theme--header",
      },
      {
        field: "activationStatus",
        width: 125,
        headerName: "Activation Status",
        headerClassName: "app-theme--header",
      },
      {
        field: "firstName",
        width: 125,
        headerName: "First Name",
        headerClassName: "app-theme--header",
      },
      {
        field: "lastName",
        width: 125,
        headerName: "Last Name",
        headerClassName: "app-theme--header",
      },
      {
        field: "addressLine1",
        width: 125,
        valueGetter: (params) => params.row?.address?.addressLine1,
        headerName: "Address Line1",
        headerClassName: "app-theme--header",
      },
      {
        field: "addressLine2",
        width: 125,
        valueGetter: (params) => params.row?.address?.addressLine2,
        headerName: "Address Line2",
        headerClassName: "app-theme--header",
      },
      {
        field: "email",
        width: 70,
        headerName: "Email",
        headerClassName: "app-theme--header",
      },
      {
        field: "includeInDirectory",
        valueGetter: (params) => params.row?.profile?.includeInDirectory,
        headerName: "Include in Directory",
        headerClassName: "app-theme--header",
      },
      {
        field: "deathDate",
        width: 70,
        headerName: "deathDate",
        headerClassName: "app-theme--header",
      },
      {
        field: "type0",
        valueGetter: (params) => params.row?.contactInfo?.phones[0]?.type,
        headerName: "Type",
        headerClassName: "app-theme--header",
      },
      {
        field: "value0",
        valueGetter: (params) => params.row?.contactInfo?.phones[0]?.value,
        headerName: "Value",
        headerClassName: "app-theme--header",
      },
      {
        field: "preferred0",
        valueGetter: (params) => params.row?.contactInfo?.phones[0]?.preferred,
        headerName: "Preferred",
        headerClassName: "app-theme--header",
      },
      {
        field: "hidden0",
        valueGetter: (params) => params.row?.contactInfo?.phones[0]?.hidden,
        headerName: "Hidden",
        headerClassName: "app-theme--header",
      },
      {
        field: "type1",
        valueGetter: (params) => params.row?.contactInfo?.phones[1]?.type,
        headerName: "Type",
        headerClassName: "app-theme--header",
      },
      {
        field: "value1",
        valueGetter: (params) => params.row?.contactInfo?.phones[1]?.value,
        headerName: "Value",
        headerClassName: "app-theme--header",
      },
      {
        field: "preferred1",
        valueGetter: (params) => params.row?.contactInfo?.phones[1]?.preferred,
        headerName: "Preferred",
        headerClassName: "app-theme--header",
      },
      {
        field: "hidden1",
        valueGetter: (params) => params.row?.contactInfo?.phones[1]?.hidden,
        headerName: "Hidden",
        headerClassName: "app-theme--header",
      },
      {
        field: "type2",
        valueGetter: (params) => params.row?.contactInfo?.phones[2]?.type,
        headerName: "Type",
        headerClassName: "app-theme--header",
      },
      {
        field: "value2",
        valueGetter: (params) => params.row?.contactInfo?.phones[2]?.value,
        headerName: "Value",
        headerClassName: "app-theme--header",
      },
      {
        field: "preferred2",
        valueGetter: (params) => params.row?.contactInfo?.phones[2]?.preferred,
        headerName: "Preferred",
        headerClassName: "app-theme--header",
      },
      {
        field: "hidden2",
        valueGetter: (params) => params.row?.contactInfo?.phones[2]?.hidden,
        headerName: "Hidden",
        headerClassName: "app-theme--header",
      },
      {
        field: "preferredName",
        width: 125,
        headerName: "Preferred Name",
        headerClassName: "app-theme--header",
      },
      {
        field: "preferredFirstName",
        width: 125,
        headerName: "Preferred First Name",
        headerClassName: "app-theme--header",
      },
      {
        field: "aboutMe",
        width: 125,
        valueGetter: (params) => params.row?.profile?.aboutMe,
        headerName: "About Me",
        headerClassName: "app-theme--header",
      },
      {
        field: "birthPlace",
        width: 125,
        valueGetter: (params) => params.row?.profile?.birthPlace,
        headerName: "Birth Place",
        headerClassName: "app-theme--header",
      },
      {
        field: "favoriteThings",
        width: 125,
        valueGetter: (params) => params.row?.profile?.favoriteThings,
        headerName: "Favorite Things",
        headerClassName: "app-theme--header",
      },
      {
        field: "interests",
        width: 125,
        valueGetter: (params) => params.row?.profile?.interests,
        headerName: "Interests",
        headerClassName: "app-theme--header",
      },
      {
        field: "profession",
        width: 125,
        valueGetter: (params) => params.row?.profile?.profession,
        headerName: "Profession",
        headerClassName: "app-theme--header",
      },
    ],
    []
  );

  return (
    <div style={{ flexGrow: 1, width: "100%", overflow: "auto" }}>
      <StripedDataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: {
              pageSize,
            },
          },
        }}
        pageSizeOptions={[pageSize]}
        rows={filteredResidents}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        rowCount={totalRowCount ?? Number.MAX_VALUE}
        paginationMode="server"
        onPaginationModelChange={handlePaginationChange}
        loading={isLoading}
        hideFooterPagination={isLoading}
        localeText={{
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to }) =>
              `${from} - ${to} of ${totalRowCount ?? "many"}`,
          },
        }}
      />
    </div>
  );
};

export default ResidentDataGrid;
